import { MAX_LEVEL } from '../../content/balance';
import { Rng } from '../../core/rng';
import { xpToNext } from '../../game/character';
import { ITEMS } from '../../game/items';
import type { QuestDef, QuestObjective, QuestRewards } from '../../game/quests';
import { registry } from '../../sim/registry';
import type { Grade } from '../../sim/types';
import type { ZoneLayout } from '../../zones/types';

/**
 * Bounty Board tasks (≈ Una's Tasks, research 02 §5.2). Each day the board
 * offers three dailies and each week three weeklies, rolled from task
 * templates against the zones the character has unlocked. A bounty is a
 * regular `QuestDef` (kind daily/weekly) so kill/gather tracking reuses the
 * quest engine. Its id encodes every parameter, so the definition can be
 * rebuilt after a reload without storing it.
 */

export type BountyKind = 'daily' | 'weekly';
export const BOUNTY_NPC = 'bounty_master_quill';
export const BOUNTY_PICKS = 3;
const PREFIX = 'bty';

// ---------------------------------------------------------------- world context

export interface BountyPack {
  def: string;
  name: string;
  grade: Grade;
  level: number;
  x: number;
  z: number;
  fieldBoss: boolean;
}

export interface BountyZone {
  id: string;
  name: string;
  kind: string;
  levelRange: [number, number];
  packs: BountyPack[];
  /** Gatherable item ids found in the zone. */
  nodes: string[];
  landmarks: { id: string; name: string; x: number; z: number }[];
  dungeons: { id: string; name: string; minLevel: number; boss: string; bossName: string }[];
}

export interface BountyContext {
  zones: BountyZone[];
}

const NODE_ITEM: Record<string, string> = { herb: 'herb', timber: 'timber', ore: 'ore_raw' };

/** Distils a zone layout into what bounty templates need. */
export function bountyZone(
  layout: ZoneLayout,
  dungeon: (id: string) => ZoneLayout | null = () => null,
): BountyZone {
  const packs: BountyPack[] = [];
  for (const p of layout.packs) {
    for (const m of p.monsters) {
      const def = registry.monsters.get(m.def);
      if (!def || packs.some((x) => x.def === m.def && x.level <= p.level)) continue;
      packs.push({
        def: m.def,
        name: def.name,
        grade: def.grade,
        level: p.level,
        x: p.x,
        z: p.z,
        fieldBoss: !!p.tags?.includes('fieldBoss'),
      });
    }
  }
  const nodes = new Set<string>();
  const landmarks: BountyZone['landmarks'] = [];
  for (const it of layout.interactables) {
    if (it.kind === 'gather') {
      const item = NODE_ITEM[String(it.data?.node ?? 'herb')];
      if (item) nodes.add(item);
    } else if (it.kind === 'fish') nodes.add('fish');
    else if (it.kind === 'dig') nodes.add('relic_fragment');
    else if ((it.kind === 'vista' || it.kind === 'waystone') && it.data?.name)
      landmarks.push({ id: it.id, name: String(it.data.name), x: it.x, z: it.z });
  }
  const dungeons: BountyZone['dungeons'] = [];
  for (const pt of layout.portals) {
    if (pt.kind !== 'dungeon') continue;
    const d = dungeon(pt.to);
    const bossPack = d?.packs.find((p) => p.tags?.includes('boss'));
    const boss = bossPack?.monsters[0]?.def;
    const bossDef = boss ? registry.monsters.get(boss) : undefined;
    if (!d || !boss || !bossDef) continue;
    dungeons.push({
      id: d.id,
      name: d.name,
      minLevel: pt.minLevel ?? d.levelRange[0],
      boss,
      bossName: bossDef.name,
    });
  }
  return {
    id: layout.id,
    name: layout.name,
    kind: layout.kind,
    levelRange: [layout.levelRange[0], layout.levelRange[1]],
    packs,
    nodes: [...nodes],
    landmarks,
    dungeons,
  };
}

// ---------------------------------------------------------------- ids

export interface BountyParams {
  kind: BountyKind;
  period: number;
  slot: number;
  tpl: string;
  target: string;
  zone: string;
  count: number;
  level: number;
}

export const isBountyId = (id: string): boolean => id.startsWith(`${PREFIX}.`);

export function encodeBounty(p: BountyParams): string {
  return [
    PREFIX,
    p.kind === 'daily' ? 'd' : 'w',
    p.period,
    p.slot,
    p.tpl,
    p.target || '-',
    p.zone || '-',
    p.count,
    p.level,
  ].join('.');
}

export function decodeBounty(id: string): BountyParams | null {
  const parts = id.split('.');
  if (parts.length !== 9 || parts[0] !== PREFIX) return null;
  const [, k, period, slot, tpl, target, zone, count, level] = parts as [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
  ];
  const nums = [period, slot, count, level].map(Number);
  if (nums.some((n) => !Number.isFinite(n)) || (k !== 'd' && k !== 'w')) return null;
  return {
    kind: k === 'd' ? 'daily' : 'weekly',
    period: nums[0] ?? 0,
    slot: nums[1] ?? 0,
    tpl,
    target,
    zone,
    count: nums[2] ?? 1,
    level: nums[3] ?? 1,
  };
}

// ---------------------------------------------------------------- templates

interface Rolled {
  target: string;
  zone: string;
  count: number;
}

interface Described {
  name: string;
  objective: QuestObjective;
  blurb: string;
}

export interface BountyTemplate {
  id: string;
  kind: BountyKind;
  weight: number;
  /** Reward multiplier and difficulty stars (1–3). */
  diff: 1 | 2 | 3;
  minLevel?: number;
  /** Short category shown on the board. */
  tag: string;
  roll(rng: Rng, level: number, ctx: BountyContext): Rolled | null;
  describe(p: BountyParams, ctx: BountyContext): Described;
}

export function plural(name: string): string {
  const words = name.split(' ');
  const last = words.pop() ?? '';
  let pl = `${last}s`;
  if (/(s|x|ch|sh)$/i.test(last)) pl = `${last}es`;
  else if (/[^aeiou]y$/i.test(last)) pl = `${last.slice(0, -1)}ies`;
  else if (/[^f]f$/i.test(last)) pl = `${last.slice(0, -1)}ves`;
  return [...words, pl].join(' ');
}

const openZones = (ctx: BountyContext, L: number, kinds = ['field']): BountyZone[] =>
  ctx.zones.filter((z) => kinds.includes(z.kind) && z.levelRange[0] <= L);
const zoneName = (ctx: BountyContext, id: string): string =>
  ctx.zones.find((z) => z.id === id)?.name ?? prettify(id);
const monsterName = (id: string): string => registry.monsters.get(id)?.name ?? prettify(id);
const itemName = (id: string): string => ITEMS[id]?.name ?? prettify(id);
const prettify = (id: string): string =>
  id.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

function findPack(ctx: BountyContext, zone: string, def: string): BountyPack | undefined {
  return ctx.zones.find((z) => z.id === zone)?.packs.find((p) => p.def === def);
}

/** Packs within reach of level `L`, weighted towards the player's level. */
function pickPack(
  rng: Rng,
  L: number,
  ctx: BountyContext,
  pred: (p: BountyPack) => boolean,
  slack = 3,
): { zone: BountyZone; pack: BountyPack } | null {
  const opts: { weight: number; value: { zone: BountyZone; pack: BountyPack } }[] = [];
  for (const zone of openZones(ctx, L))
    for (const pack of zone.packs)
      if (pred(pack) && pack.level <= L + slack)
        opts.push({ weight: 1 / (1 + Math.abs(pack.level - L)), value: { zone, pack } });
  return opts.length ? rng.weighted(opts) : null;
}

function pickNode(
  rng: Rng,
  L: number,
  ctx: BountyContext,
  items: string[],
): { zone: BountyZone; item: string } | null {
  const opts: { weight: number; value: { zone: BountyZone; item: string } }[] = [];
  for (const zone of openZones(ctx, L)) {
    const inBand = L <= zone.levelRange[1] + 2;
    for (const item of zone.nodes)
      if (items.includes(item)) opts.push({ weight: inBand ? 3 : 1, value: { zone, item } });
  }
  return opts.length ? rng.weighted(opts) : null;
}

const isTrash = (p: BountyPack) => (p.grade === 'normal' || p.grade === 'minion') && !p.fieldBoss;
const isElite = (p: BountyPack) => p.grade === 'elite' && !p.fieldBoss;
const hasFieldBoss = (ctx: BountyContext, L: number) =>
  openZones(ctx, L).some((z) => z.packs.some((p) => p.fieldBoss && p.level <= L + 1));

function killObjective(ctx: BountyContext, p: BountyParams, text: string): QuestObjective {
  const pack = findPack(ctx, p.zone, p.target);
  return {
    type: 'kill',
    target: p.target,
    count: p.count,
    text,
    zone: p.zone,
    ...(pack ? { pos: { x: pack.x, z: pack.z }, radius: 10 } : {}),
  };
}

const counterTask =
  (target: string, text: string, name: string, blurb: string) =>
  (p: BountyParams): Described => ({
    name,
    blurb,
    objective: { type: 'use', target, count: p.count, text },
  });

const gatherTemplate = (
  id: string,
  kind: BountyKind,
  item: string,
  tag: string,
  count: (L: number) => number,
): BountyTemplate => ({
  id,
  kind,
  weight: kind === 'daily' ? 1 : 2,
  diff: kind === 'daily' ? 1 : 2,
  tag,
  roll: (rng, L, ctx) => {
    const n = pickNode(
      rng,
      L,
      ctx,
      item === '*' ? ['herb', 'timber', 'ore_raw', 'fish', 'relic_fragment'] : [item],
    );
    return n
      ? { target: n.item, zone: n.zone.id, count: kind === 'daily' ? count(L) : count(L) * 5 }
      : null;
  },
  describe: (p, ctx) => ({
    name: `${kind === 'daily' ? 'Gathering' : 'Stockpile'}: ${itemName(p.target)}`,
    blurb: `The quartermasters are short of ${itemName(p.target).toLowerCase()}. ${zoneName(ctx, p.zone)} has plenty, if you know where to look.`,
    objective: {
      type: 'collect',
      target: p.target,
      count: p.count,
      text: `Gather ${itemName(p.target)}`,
      zone: p.zone,
    },
  }),
});

const bulkCount = (L: number) => 6 + Math.floor(L / 4);
const rareCount = (L: number) => 3 + Math.floor(L / 8);

export const TEMPLATES: BountyTemplate[] = [
  // ------------------------------------------------ dailies
  {
    id: 'hunt',
    kind: 'daily',
    weight: 3,
    diff: 1,
    tag: 'Hunt',
    roll: (rng, L, ctx) => {
      const r = pickPack(rng, L, ctx, isTrash);
      return r ? { target: r.pack.def, zone: r.zone.id, count: 6 + Math.round(L * 0.25) } : null;
    },
    describe: (p, ctx) => {
      const pl = plural(monsterName(p.target));
      return {
        name: `Hunt: ${pl}`,
        blurb: `${pl} have been harrying the roads of ${zoneName(ctx, p.zone)}. Thin their numbers.`,
        objective: killObjective(ctx, p, `Defeat ${pl}`),
      };
    },
  },
  {
    id: 'elite',
    kind: 'daily',
    weight: 1.5,
    diff: 2,
    tag: 'Elite',
    roll: (rng, L, ctx) => {
      const r = pickPack(rng, L, ctx, isElite);
      return r ? { target: r.pack.def, zone: r.zone.id, count: L >= 20 ? 3 : 2 } : null;
    },
    describe: (p, ctx) => {
      const n = monsterName(p.target);
      return {
        name: `Elite Bounty: ${n}`,
        blurb: `A ${n.toLowerCase()} leads the packs in ${zoneName(ctx, p.zone)}. Bring it down.`,
        objective: killObjective(ctx, p, `Defeat ${plural(n)}`),
      };
    },
  },
  {
    id: 'cull',
    kind: 'daily',
    weight: 1,
    diff: 1,
    tag: 'Hunt',
    roll: (_rng, L) => ({ target: '*', zone: '-', count: 20 + L }),
    describe: (p) => ({
      name: 'Cull the Hollow-touched',
      blurb: 'Anything with shard-light in its eyes is fair game today.',
      objective: { type: 'kill', target: '*', count: p.count, text: 'Defeat monsters anywhere' },
    }),
  },
  gatherTemplate('forage', 'daily', 'herb', 'Gather', bulkCount),
  gatherTemplate('lumber', 'daily', 'timber', 'Gather', bulkCount),
  gatherTemplate('mine', 'daily', 'ore_raw', 'Gather', bulkCount),
  gatherTemplate('angle', 'daily', 'fish', 'Gather', rareCount),
  gatherTemplate('dig', 'daily', 'relic_fragment', 'Gather', rareCount),
  {
    id: 'survey',
    kind: 'daily',
    weight: 1.2,
    diff: 1,
    tag: 'Explore',
    roll: (rng, L, ctx) => {
      const opts = openZones(ctx, L, ['field', 'town']).flatMap((z) =>
        z.landmarks.map((l) => ({ zone: z, l })),
      );
      if (!opts.length) return null;
      const o = rng.pick(opts);
      return { target: o.l.id, zone: o.zone.id, count: 1 };
    },
    describe: (p, ctx) => {
      const lm = ctx.zones.find((z) => z.id === p.zone)?.landmarks.find((l) => l.id === p.target);
      const name = lm?.name ?? prettify(p.target);
      const pos = lm ? { x: lm.x, z: lm.z } : undefined;
      return {
        name: `Survey: ${name}`,
        blurb: `The cartographers want fresh eyes on ${name} in ${zoneName(ctx, p.zone)}. Go and look.`,
        objective: {
          type: 'reach',
          target: `${p.zone}:${pos ? `${Math.round(pos.x)},${Math.round(pos.z)}` : p.target}`,
          count: 1,
          text: `Visit ${name}`,
          zone: p.zone,
          pos,
          radius: 8,
        },
      };
    },
  },
  {
    id: 'fieldboss',
    kind: 'daily',
    weight: 1.2,
    diff: 3,
    tag: 'Boss',
    roll: (rng, L, ctx) => {
      const r = pickPack(rng, L, ctx, (p) => p.fieldBoss, 1);
      return r ? { target: r.pack.def, zone: r.zone.id, count: 1 } : null;
    },
    describe: (p, ctx) => {
      const n = monsterName(p.target);
      return {
        name: `Wanted: ${n}`,
        blurb: `${n} has been sighted in ${zoneName(ctx, p.zone)}. The bounty is posted — dead, not captured.`,
        objective: killObjective(ctx, p, `Defeat ${n}`),
      };
    },
  },
  {
    id: 'dungeon',
    kind: 'daily',
    weight: 1,
    diff: 3,
    tag: 'Dungeon',
    roll: (rng, L, ctx) => {
      const opts = openZones(ctx, L).flatMap((z) => z.dungeons.filter((d) => d.minLevel <= L));
      if (!opts.length) return null;
      const d = rng.pick(opts);
      return { target: d.boss, zone: d.id, count: 1 };
    },
    describe: (p, ctx) => {
      const d = ctx.zones.flatMap((z) => z.dungeons).find((x) => x.id === p.zone);
      const place = d?.name ?? prettify(p.zone);
      const boss = d?.bossName ?? monsterName(p.target);
      return {
        name: `Delve: ${place}`,
        blurb: `Something stirs again in ${place}. Put ${boss} back in the ground.`,
        objective: {
          type: 'kill',
          target: p.target,
          count: 1,
          text: `Defeat ${boss} in ${place}`,
          zone: p.zone,
        },
      };
    },
  },
  {
    id: 'battle',
    kind: 'daily',
    weight: 1,
    diff: 1,
    minLevel: 3,
    tag: 'Supplies',
    roll: (_rng, L) => ({ target: 'ctr:battle', zone: '-', count: L >= 20 ? 3 : 2 }),
    describe: counterTask(
      'ctr:battle',
      'Use battle items (keys 1–4)',
      'Field Test',
      'Tessa wants to know if her new batch holds up in a real fight.',
    ),
  },
  {
    id: 'temper',
    kind: 'daily',
    weight: 1,
    diff: 1,
    minLevel: 12,
    tag: 'Forge',
    roll: () => ({ target: 'ctr:temper', zone: '-', count: 1 }),
    describe: counterTask(
      'ctr:temper',
      'Attempt a tempering at the forge',
      'Forge Work',
      'Borin needs the forge kept hot. Temper something — anything.',
    ),
  },
  {
    id: 'rift',
    kind: 'daily',
    weight: 2,
    diff: 2,
    minLevel: MAX_LEVEL,
    tag: 'Rift',
    roll: () => ({ target: 'ctr:rift', zone: '-', count: 1 }),
    describe: counterTask(
      'ctr:rift',
      'Complete a Rift Delve run',
      'Rift Watch',
      'Elowen measured a spike in the rift. Go in and close what you can.',
    ),
  },
  {
    id: 'colossus',
    kind: 'daily',
    weight: 2,
    diff: 2,
    minLevel: MAX_LEVEL,
    tag: 'Colossus',
    roll: () => ({ target: 'ctr:colossus', zone: '-', count: 1 }),
    describe: counterTask(
      'ctr:colossus',
      'Defeat a colossus',
      'Colossus Hunt',
      'The hunters’ lodge will pay for proof of a colossus kill.',
    ),
  },
  // ------------------------------------------------ weeklies
  {
    id: 'w_cull',
    kind: 'weekly',
    weight: 2,
    diff: 3,
    tag: 'Hunt',
    roll: (_rng, L) => ({ target: '*', zone: '-', count: 100 + 3 * L }),
    describe: (p) => ({
      name: 'The Great Cull',
      blurb: 'The Watch wants the roads clear by week’s end. Every kill counts.',
      objective: { type: 'kill', target: '*', count: p.count, text: 'Defeat monsters anywhere' },
    }),
  },
  {
    id: 'w_elite',
    kind: 'weekly',
    weight: 1.5,
    diff: 3,
    tag: 'Elite',
    roll: (rng, L, ctx) => {
      const r = pickPack(rng, L, ctx, isElite);
      return r ? { target: r.pack.def, zone: r.zone.id, count: 6 } : null;
    },
    describe: (p, ctx) => {
      const n = plural(monsterName(p.target));
      return {
        name: `Warband Breaker: ${n}`,
        blurb: `Break the ${n.toLowerCase()} of ${zoneName(ctx, p.zone)} before they gather a host.`,
        objective: killObjective(ctx, p, `Defeat ${n}`),
      };
    },
  },
  gatherTemplate('w_gather', 'weekly', '*', 'Gather', (L) => 6 + Math.floor(L / 3)),
  {
    id: 'w_dailies',
    kind: 'weekly',
    weight: 2,
    diff: 2,
    tag: 'Board',
    roll: () => ({ target: 'ctr:dailies', zone: '-', count: 5 }),
    describe: counterTask(
      'ctr:dailies',
      'Claim daily bounties',
      'Board Regular',
      'Quill keeps a ledger. Fill a page of it this week.',
    ),
  },
  {
    id: 'w_fieldboss',
    kind: 'weekly',
    weight: 1,
    diff: 3,
    tag: 'Boss',
    roll: (_rng, L, ctx) =>
      hasFieldBoss(ctx, L) ? { target: 'ctr:fieldboss', zone: '-', count: 2 } : null,
    describe: counterTask(
      'ctr:fieldboss',
      'Defeat field bosses',
      'Trophy Hunter',
      'Two trophies for the lodge wall. Field bosses only.',
    ),
  },
  {
    id: 'w_battle',
    kind: 'weekly',
    weight: 1,
    diff: 2,
    minLevel: 3,
    tag: 'Supplies',
    roll: () => ({ target: 'ctr:battle', zone: '-', count: 8 }),
    describe: counterTask(
      'ctr:battle',
      'Use battle items (keys 1–4)',
      'Quartermaster’s Trial',
      'Burn through a crate of battle items and report what worked.',
    ),
  },
  {
    id: 'w_temper',
    kind: 'weekly',
    weight: 1,
    diff: 2,
    minLevel: 12,
    tag: 'Forge',
    roll: () => ({ target: 'ctr:temper', zone: '-', count: 5 }),
    describe: counterTask(
      'ctr:temper',
      'Attempt tempering at the forge',
      'Forge Fire',
      'Five attempts at the anvil, whatever the outcome.',
    ),
  },
  {
    id: 'w_rift',
    kind: 'weekly',
    weight: 2,
    diff: 3,
    minLevel: MAX_LEVEL,
    tag: 'Rift',
    roll: () => ({ target: 'ctr:rift', zone: '-', count: 3 }),
    describe: counterTask(
      'ctr:rift',
      'Complete Rift Delve runs',
      'Rift Sweep',
      'Three delves this week. Elowen will chart what you find.',
    ),
  },
  {
    id: 'w_colossus',
    kind: 'weekly',
    weight: 2,
    diff: 3,
    minLevel: MAX_LEVEL,
    tag: 'Colossus',
    roll: () => ({ target: 'ctr:colossus', zone: '-', count: 3 }),
    describe: counterTask(
      'ctr:colossus',
      'Defeat colossi',
      'Colossus Season',
      'Three colossi before the week turns. The lodge pays in leap crystal.',
    ),
  },
  {
    id: 'w_deepvault',
    kind: 'weekly',
    weight: 1.5,
    diff: 3,
    minLevel: MAX_LEVEL,
    tag: 'Deepvault',
    roll: () => ({ target: 'ctr:deepvault', zone: '-', count: 1 }),
    describe: counterTask(
      'ctr:deepvault',
      'Clear a Deepvault gate',
      'Into the Reliquary',
      'The Sunken Reliquary opened again. Clear a gate and bring back its seal.',
    ),
  },
  {
    id: 'w_siege',
    kind: 'weekly',
    weight: 1.5,
    diff: 3,
    minLevel: MAX_LEVEL,
    tag: 'Siege',
    roll: () => ({ target: 'ctr:siege', zone: '-', count: 1 }),
    describe: counterTask(
      'ctr:siege',
      'Clear a Warlord Siege gate',
      'Siege of the Hollow Throne',
      'The Warden asks for a gate of the Hollow Throne. Any gate.',
    ),
  },
];

export const TEMPLATE_BY_ID: Record<string, BountyTemplate> = Object.fromEntries(
  TEMPLATES.map((t) => [t.id, t]),
);

// ---------------------------------------------------------------- generation

/**
 * Rolls the board for one character and period: three bounties from
 * distinct templates, feasible at `level`. Same inputs → same ids.
 */
export function generateBounties(
  kind: BountyKind,
  period: number,
  seed: string,
  level: number,
  ctx: BountyContext,
): string[] {
  const rng = new Rng(`${seed}:${kind}:${period}`);
  const used = new Set<string>();
  const out: string[] = [];
  for (let guard = 0; out.length < BOUNTY_PICKS && guard < 40; guard++) {
    const cands = TEMPLATES.filter(
      (t) => t.kind === kind && !used.has(t.id) && level >= (t.minLevel ?? 1),
    );
    if (!cands.length) break;
    const t = rng.weighted(cands.map((x) => ({ weight: x.weight, value: x })));
    used.add(t.id);
    const r = t.roll(rng, level, ctx);
    if (r) out.push(encodeBounty({ kind, period, slot: out.length, tpl: t.id, ...r, level }));
  }
  return out;
}

// ---------------------------------------------------------------- rewards & quest defs

const roundTo = (n: number, step: number) => Math.round(n / step) * step;

export function bountyRewards(kind: BountyKind, level: number, diff: number): QuestRewards {
  const need = xpToNext(Math.max(1, Math.min(level, MAX_LEVEL - 1)));
  const endgame = level >= MAX_LEVEL;
  const items: { id: string; count: number }[] = [];
  if (kind === 'daily') {
    items.push({ id: 'bounty_seal', count: diff });
    if (level >= 10) items.push({ id: 'aether_shard', count: (10 + 5 * level) * diff });
    if (endgame) items.push({ id: 'leap_crystal', count: 2 * diff });
    return {
      xp: Math.round(need * 0.06 * diff),
      silver: roundTo((60 + 35 * level) * diff, 10),
      gold: endgame ? 3 * diff : 0,
      items,
    };
  }
  items.push({ id: 'bounty_seal', count: 4 * diff });
  if (level >= 10) items.push({ id: 'aether_shard', count: (40 + 20 * level) * diff });
  if (level >= 20) items.push({ id: 'leap_crystal', count: 3 * diff });
  return {
    xp: Math.round(need * 0.2 * diff),
    silver: roundTo((300 + 120 * level) * diff, 50),
    gold: endgame ? 25 * diff : level >= 12 ? 4 * diff : 0,
    items,
  };
}

/** Rebuilds the quest definition for a bounty id (null if it can't be decoded). */
export function bountyQuest(id: string, ctx: BountyContext): QuestDef | null {
  const p = decodeBounty(id);
  const t = p && TEMPLATE_BY_ID[p.tpl];
  if (!p || !t) return null;
  const d = t.describe(p, ctx);
  return {
    id,
    kind: p.kind,
    name: d.name,
    level: p.level,
    zone: p.zone !== '-' ? p.zone : 'havenreach',
    giver: BOUNTY_NPC,
    turnIn: BOUNTY_NPC,
    steps: [{ objectives: [d.objective] }],
    dialogue: {
      offer: [d.blurb],
      progress: 'Proof first, coin after.',
      complete: ['Quill stamps the notice. “Clean work. Here is your pay.”'],
    },
    rewards: bountyRewards(p.kind, p.level, t.diff),
  };
}

export function bountyTemplate(id: string): BountyTemplate | undefined {
  const p = decodeBounty(id);
  return p ? TEMPLATE_BY_ID[p.tpl] : undefined;
}

// ---------------------------------------------------------------- reputation

export const REP_TIERS = [0, 15, 50, 120, 250];
export const REP_NAMES = [
  'Unknown',
  'Known Face',
  'Trusted Hand',
  'Renowned',
  'Legend of the Board',
];

/** Bounty standing from lifetime seals earned: tier index and progress to the next. */
export function repTier(seals: number): { tier: number; name: string; next: number | null } {
  let tier = 0;
  REP_TIERS.forEach((t, i) => {
    if (seals >= t) tier = i;
  });
  return { tier, name: REP_NAMES[tier] ?? '', next: REP_TIERS[tier + 1] ?? null };
}
